import type { ComponentType } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ProgressStep { key: string; label: string; icon?: ComponentType<{ className?: string }> }

/**
 * A record's journey as a track: Lead → Working → Qualified → Converted. Steps already reached
 * are white rings on a filled line, the line fades out after the current step, and steps still
 * ahead are dark dots. When a record leaves the path (lost, declined, cancelled) the next node is
 * a red end marker carrying that outcome. Steps can be clickable to move the record.
 */
export default function StatusProgress({ steps, current, exit, onSelect, canSelect, dark, className }: {
  steps: ProgressStep[];
  /** Key of the step the record is at. With `exit`, the last step it reached. */
  current: string | null;
  /** The record left the normal path, e.g. { label: "Lost" }. */
  exit?: { label: string } | null;
  onSelect?: (key: string) => void;
  canSelect?: (key: string) => boolean;
  dark?: boolean;
  className?: string;
}) {
  const reached = Math.max(0, steps.findIndex((s) => s.key === current));
  // With an exit, the path is cut after the last reached step and ends on the exit marker.
  const nodes = exit ? [...steps.slice(0, reached + 1), { key: "__exit", label: exit.label }] : steps;
  const at = exit ? nodes.length - 1 : reached;
  const span = Math.max(1, nodes.length - 1);
  const filled = (at / span) * 100;
  const fade = exit || at >= span ? 0 : (1 / span) * 100;

  return (
    <div className={cn("relative select-none px-1 pb-1", className)}>
      {/* Track, from the first node's centre to the last one's. */}
      <div className="absolute left-[1.375rem] right-[1.375rem] top-[1.125rem] h-[3px] -translate-y-1/2 rounded-full" aria-hidden>
        <div className={cn("absolute inset-0 rounded-full", dark ? "bg-white/10" : "bg-gray-200/80")} />
        <div className={cn("absolute inset-y-0 left-0 rounded-full", exit ? "bg-gradient-to-r from-primary to-red-500" : "bg-primary")} style={{ width: `${filled}%` }} />
        {fade > 0 && <div className="absolute inset-y-0 rounded-r-full bg-gradient-to-r from-primary/70 to-transparent" style={{ left: `${filled}%`, width: `${fade}%` }} />}
      </div>

      {/* Nodes sit exactly on the track: node i at i / (n - 1) of its length. */}
      <ol className="relative mx-[1.125rem] h-[4.75rem]">
        {nodes.map((step, i) => {
          const isExit = step.key === "__exit";
          const done = i < at;
          const isCurrent = i === at;
          const ahead = i > at;
          const Icon = "icon" in step && step.icon ? step.icon : null;
          const clickable = !!onSelect && !isExit && !isCurrent && (canSelect ? canSelect(step.key) : true);
          const first = i === 0;
          const last = i === nodes.length - 1;
          const node = (
            <span
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full transition-all",
                isExit && "bg-red-500 text-white ring-4 ring-red-500/15",
                !isExit && (done || isCurrent) && cn("border-[3px] border-primary text-primary", dark ? "bg-black" : "bg-white"),
                !isExit && isCurrent && "shadow-[0_0_0_5px_rgba(255,109,39,0.15)]",
                !isExit && ahead && (dark ? "bg-gray-800 text-gray-400" : "bg-gray-900 text-white"),
                clickable && "group-hover:scale-110",
              )}
            >
              {isExit ? <X className="h-4 w-4" />
                : Icon ? <Icon className="h-3.5 w-3.5" />
                : done ? <Check className="h-4 w-4" strokeWidth={3} />
                : isCurrent ? <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                : <span className="text-[10px] font-semibold">{i + 1}</span>}
            </span>
          );
          // Each label gets one step's share of the track and wraps to two lines; end labels grow inward.
          const room = `calc(${100 / span}% - ${first || last ? 14 : 8}px)`;
          const label = (
            <span className={cn(
              "mt-2 line-clamp-2 block w-full break-words hyphens-auto leading-tight", nodes.length >= 5 ? "text-[10px] sm:text-[11px]" : "text-[11px] sm:text-xs",
              first ? "text-left" : last ? "text-right" : "text-center",
              isExit ? "font-semibold text-red-500" : isCurrent ? cn("font-semibold", dark ? "text-white" : "text-gray-900") : dark ? "text-gray-400" : "text-gray-500",
            )}>{step.label}</span>
          );
          // The first and last labels hug the edges; the rest are centred under their node.
          const align = first ? "items-start" : last ? "items-end" : "items-center";
          const shift = first ? "-translate-x-[1.125rem]" : last ? "-translate-x-[calc(100%-1.125rem)]" : "-translate-x-1/2";
          const body = <>{node}{label}</>;
          return (
            <li key={step.key} className={cn("absolute top-0 flex flex-col", align, shift)} style={{ left: `${(i / span) * 100}%`, width: room, maxWidth: "8rem" }} aria-current={isCurrent ? "step" : undefined}>
              {clickable ? (
                <button type="button" onClick={() => onSelect!(step.key)} title={`Move to ${step.label}`} className={cn("group flex w-full flex-col focus:outline-none", align)}>{body}</button>
              ) : (
                <div className={cn("flex w-full flex-col", align)}>{body}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
